import * as React from 'react';
import { FC, useState, useCallback, useMemo, useEffect } from 'react';
import { ListGroupItem, ListGroup, Badge, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from 'reactstrap';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { IconButton } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import PackConfigurationModal from './PackConfigurationModal';
import { PackSettings } from '../../../models';
import { serviceName } from '../../../shared/appointmint/appointment';
import { useGeneralSettings } from '../../../store/settings/hooks';
import { createDefaultPackSettings } from './packDefaults';
import { useDispatch } from 'react-redux';
import { alertError, alertSuccess } from '../../../store/alerts/actions';
import { loadPackTemplates } from '../../../store/settings/actions';
import { getErrorMessage } from '../../../ajax/errors';
import { usePackTemplates } from '../../../hooks/usePackTemplates';
import {
    createPackTemplateAsync,
    updatePackTemplateAsync,
    deletePackTemplateAsync,
} from '../../../ajax/requests';
import AppButton from '../../../components/AppButton';

export interface PacksSettingsProps {
    id: string,
}

const PacksSettings : FC<PacksSettingsProps> = ({ id }) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const settings = useGeneralSettings();

    const { templates } = usePackTemplates();

    const [editingPack, setEditingPack] = useState<PackSettings|undefined>(undefined);
    const [editingTemplateId, setEditingTemplateId] = useState<string|undefined>(undefined);
    const [deletingTemplateId, setDeletingTemplateId] = React.useState<string|undefined>(undefined);
    const [saving, setSaving] = useState(false);

    const closeDeleteModal = React.useCallback(() => setDeletingTemplateId(undefined), []);
    const onAddNew = useCallback(() => {
        setEditingTemplateId(undefined);
        setEditingPack(createDefaultPackSettings());
    }, []);
    const onSelect = useCallback((pack: PackSettings) => () => {
        setEditingTemplateId(pack.templateId);
        setEditingPack(pack);
    }, []);
    const onClosePack = useCallback(() => {
        setEditingPack(undefined);
        setEditingTemplateId(undefined);
    }, []);

    const onRequestDelete = useCallback((packId: string) => {
        const pack = templates.find(p => p.id === packId);
        if (pack?.templateId) {
            setDeletingTemplateId(pack.templateId);
        }
    }, [templates]);

    const onSavePack = useCallback(async (newPack: PackSettings, applyToAllVersions?: boolean) => {
        setSaving(true);
        try {
            if (editingTemplateId) {
                await updatePackTemplateAsync(editingTemplateId, { ...newPack, templateId: editingTemplateId }, applyToAllVersions);
                dispatch(alertSuccess(t(TK.savedSuccessfully)));
            } else {
                await createPackTemplateAsync({ ...newPack, id: '', templateId: '' });
                dispatch(alertSuccess(t(TK.entityCreatedSuccessfully)));
            }
            setEditingPack(undefined);
            setEditingTemplateId(undefined);
            dispatch(loadPackTemplates(true));
        } catch (err) {
            dispatch(alertError(getErrorMessage(err) || t(TK.somethingWentWrong)));
        } finally {
            setSaving(false);
        }
    }, [editingTemplateId, dispatch, t]);

    const onConfirmDelete = useCallback(async () => {
        if (!deletingTemplateId) return;
        setSaving(true);
        try {
            await deletePackTemplateAsync(deletingTemplateId);
            dispatch(alertSuccess(t(TK.entityDeletedSuccessfully)));
            setDeletingTemplateId(undefined);
            onClosePack();
            dispatch(loadPackTemplates(true));
        } catch (err) {
            dispatch(alertError(getErrorMessage(err) || t(TK.somethingWentWrong)));
        } finally {
            setSaving(false);
        }
    }, [deletingTemplateId, onClosePack, dispatch, t]);

    const sortedPacks = useMemo(
        () => [...templates].sort((a, b) => a.name.localeCompare(b.name)),
        [templates]
    );

    useEffect(() => {
        dispatch(loadPackTemplates(true));
    }, [dispatch]);

    return (
        <>
            <Alert color="secondary" isOpen>
                {t(TK.packsAndSubscriptionsSettingsDescription)}
            </Alert>

            <ListGroup>
                { sortedPacks.map((pack) => (
                    <ListGroupItem key={pack.templateId ?? pack.id}>
                        <AppButton id={`${id}-open-${pack.templateId}-button`} onClick={onSelect(pack)} color="link">{pack.name}</AppButton>
                        {pack.appointmentTypes.map(type => <><Badge key={type}>{serviceName(type, settings)}</Badge>&nbsp;</>)}
                    </ListGroupItem>
                ))}
                <ListGroupItem>
                    <IconButton id={`${id}-add-button`} onClick={onAddNew} size="small"><AddCircleIcon/>&nbsp;{t(TK.new)}</IconButton>
                </ListGroupItem>
            </ListGroup>

            <PackConfigurationModal
                defaultValue={editingPack}
                onSave={onSavePack}
                onClose={onClosePack}
                onDelete={onRequestDelete}
            />

            <Modal isOpen={!!deletingTemplateId} toggle={closeDeleteModal}>
                <ModalHeader toggle={closeDeleteModal}>{t(TK.areYouSure)}</ModalHeader>
                <ModalBody>{t(TK.youRAboutToDelete)}</ModalBody>
                <ModalFooter>
                    <AppButton id={`${id}-confirm-delete-button`} color="danger" disabled={saving} onClick={onConfirmDelete}>{t(TK.delete)}</AppButton>{' '}
                    <AppButton id={`${id}-cancel-delete-button`} color="secondary" onClick={closeDeleteModal}>{t(TK.cancel)}</AppButton>
                </ModalFooter>
            </Modal>
        </>
    );
}

export default PacksSettings;
